import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    MAX_COLORS,
    MAX_PALETTES,
    getColorPalettes,
    loadColorPalettes,
    newPaletteId,
    normalizeHex,
    persistColorPalettes,
    subscribeColorPalettes,
} from '../utils/colorPalettes';

/** Editor da biblioteca de paletas. Salva na conta, sem depender do salvar do projeto. */
const ColorPaletteEditor = () => {
    const { t } = useTranslation('builder');
    const [data, setData] = useState(getColorPalettes);
    const [phase, setPhase] = useState('loading');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [hexDraft, setHexDraft] = useState({});
    const timer = useRef(null);
    const pending = useRef(null);
    const ready = useRef(false);

    useEffect(() => subscribeColorPalettes(setData), []);
    useEffect(() => {
        let alive = true;
        loadColorPalettes()
            .then(() => {
                if (!alive) return;
                ready.current = true;
                setPhase('ready');
            })
            .catch(() => { if (alive) setPhase('error'); });
        return () => {
            alive = false;
            if (timer.current) clearTimeout(timer.current);
        };
    }, []);

    const flush = async () => {
        const next = pending.current;
        if (!next) return;
        pending.current = null;
        setPhase('saving');
        try {
            await persistColorPalettes(next);
            setPhase(pending.current ? 'saving' : 'saved');
            if (pending.current) flush();
        } catch {
            setPhase('error');
        }
    };

    const schedule = (next) => {
        if (!ready.current) return;
        setData(next);
        pending.current = next;
        setPhase('saving');
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(flush, 400);
    };

    const palettes = data.palettes || [];
    const activeId = palettes.some((p) => p.id === data.activePaletteId) ? data.activePaletteId : (palettes[0]?.id || '');
    const current = palettes.find((p) => p.id === activeId) || null;

    const patchCurrent = (partial) => {
        if (!current) return;
        schedule({
            ...data,
            activePaletteId: activeId,
            palettes: palettes.map((p) => (p.id === current.id ? { ...p, ...partial } : p)),
        });
    };

    const addPalette = () => {
        if (palettes.length >= MAX_PALETTES) return;
        const id = newPaletteId();
        const created = {
            id,
            name: t('builder:paletteDefaultName', { defaultValue: 'Paleta {{n}}', n: palettes.length + 1 }),
            colors: [{ id: newPaletteId(), name: t('builder:paletteDefaultColor', { defaultValue: 'Primária' }), hex: '#3b82f6' }],
        };
        setConfirmDelete(false);
        schedule({ palettes: [...palettes, created], activePaletteId: id });
    };

    const removePalette = () => {
        if (!current) return;
        const nextList = palettes.filter((p) => p.id !== current.id);
        setConfirmDelete(false);
        schedule({
            palettes: nextList,
            activePaletteId: nextList[0]?.id || '',
        });
    };

    const addColor = () => {
        if (!current || current.colors.length >= MAX_COLORS) return;
        patchCurrent({
            colors: [...current.colors, { id: newPaletteId(), name: '', hex: '#64748b' }],
        });
    };

    const patchColor = (colorId, partial) => {
        if (!current) return;
        patchCurrent({
            colors: current.colors.map((c) => (c.id === colorId ? { ...c, ...partial } : c)),
        });
    };

    const removeColor = (colorId) => {
        if (!current) return;
        patchCurrent({ colors: current.colors.filter((c) => c.id !== colorId) });
    };

    const statusText = phase === 'saving'
        ? t('builder:paletteSaving', { defaultValue: 'Salvando…' })
        : phase === 'saved'
            ? t('builder:paletteSaved', { defaultValue: 'Salvo na sua conta' })
            : phase === 'error'
                ? t('builder:paletteSaveError', { defaultValue: 'Não foi possível salvar.' })
                : '';

    return (
        <div className="bc-palette-editor" data-palette-source="editor">
            <div className="bc-palette-editor-title">{t('builder:myPalettesTitle', { defaultValue: 'Minhas paletas' })}</div>
            <p className="bc-hint" style={{ marginBottom: 10, lineHeight: 1.4 }}>
                {t('builder:myPalettesHint', { defaultValue: 'As paletas ficam na sua conta e aparecem em qualquer projeto. A cor aplicada no elemento fica gravada como hex.' })}
            </p>
            {phase === 'loading' ? (
                <p className="bc-hint">{t('builder:paletteLoading', { defaultValue: 'Carregando paletas…' })}</p>
            ) : palettes.length === 0 ? (
                <p className="bc-hint">{t('builder:paletteEmpty', { defaultValue: 'Nenhuma paleta ainda.' })}</p>
            ) : (
                <>
                    <div className="bc-form-group">
                        <label className="bc-label">{t('builder:myPalettesTitle', { defaultValue: 'Minhas paletas' })}</label>
                        <select
                            className="bc-input"
                            value={activeId}
                            onChange={(e) => schedule({ ...data, activePaletteId: e.target.value })}
                        >
                            {palettes.map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                    {current && (
                        <>
                            <div className="bc-form-group">
                                <label className="bc-label">{t('builder:paletteName', { defaultValue: 'Nome da paleta' })}</label>
                                <input
                                    type="text"
                                    className="bc-input"
                                    maxLength={48}
                                    value={current.name}
                                    onChange={(e) => patchCurrent({ name: e.target.value.slice(0, 48) })}
                                />
                            </div>
                            <p className="bc-hint" style={{ marginBottom: 8 }}>
                                {data.activePaletteId === current.id
                                    ? t('builder:paletteActiveNow', { defaultValue: 'Esta é a paleta da barra Minhas cores.' })
                                    : ''}
                            </p>
                            {current.colors.map((color) => (
                                <div key={color.id} className="bc-palette-color-row">
                                    <input
                                        type="color"
                                        value={normalizeHex(color.hex) || '#64748b'}
                                        onChange={(e) => patchColor(color.id, { hex: e.target.value })}
                                        aria-label={color.name || color.hex}
                                    />
                                    <input
                                        type="text"
                                        className="bc-input"
                                        maxLength={40}
                                        value={color.name}
                                        placeholder={t('builder:paletteColorName', { defaultValue: 'Nome da cor' })}
                                        onChange={(e) => patchColor(color.id, { name: e.target.value.slice(0, 40) })}
                                    />
                                    <input
                                        type="text"
                                        className="bc-input bc-palette-hex"
                                        value={hexDraft[color.id] ?? color.hex}
                                        onChange={(e) => {
                                            const raw = e.target.value.slice(0, 7);
                                            setHexDraft((d) => ({ ...d, [color.id]: raw }));
                                            const hex = normalizeHex(raw);
                                            if (hex) patchColor(color.id, { hex });
                                        }}
                                        onBlur={() => setHexDraft((d) => {
                                            const next = { ...d };
                                            delete next[color.id];
                                            return next;
                                        })}
                                    />
                                    <button type="button" className="bc-palette-icon-btn" onClick={() => removeColor(color.id)} aria-label={t('builder:remove', { defaultValue: 'Remover' })}>×</button>
                                </div>
                            ))}
                            {current.colors.length < MAX_COLORS && (
                                <button type="button" className="bc-palette-text-btn" onClick={addColor}>
                                    {t('builder:paletteAddColor', { defaultValue: 'Adicionar cor' })}
                                </button>
                            )}
                        </>
                    )}
                </>
            )}
            <div className="bc-palette-editor-actions">
                {palettes.length < MAX_PALETTES && (
                    <button type="button" className="bc-palette-text-btn" onClick={addPalette}>
                        {t('builder:paletteNew', { defaultValue: 'Nova paleta' })}
                    </button>
                )}
                {current && !confirmDelete && (
                    <button type="button" className="bc-palette-text-btn danger" onClick={() => setConfirmDelete(true)}>
                        {t('builder:paletteDelete', { defaultValue: 'Apagar' })}
                    </button>
                )}
                {current && confirmDelete && (
                    <>
                        <span className="bc-hint">{t('builder:paletteDeleteConfirm', { defaultValue: 'Apagar esta paleta?' })}</span>
                        <button type="button" className="bc-palette-text-btn danger" onClick={removePalette}>{t('builder:paletteDelete', { defaultValue: 'Apagar' })}</button>
                        <button type="button" className="bc-palette-text-btn" onClick={() => setConfirmDelete(false)}>{t('builder:cancel', { defaultValue: 'Cancelar' })}</button>
                    </>
                )}
            </div>
            {palettes.length >= MAX_PALETTES && (
                <p className="bc-hint">{t('builder:paletteMaxPalettes', { defaultValue: 'No máximo 12 paletas.' })}</p>
            )}
            {statusText ? <p className={`bc-hint${phase === 'error' ? ' bc-palette-error' : ''}`}>{statusText}</p> : null}
        </div>
    );
};

export default ColorPaletteEditor;
